import React from 'react';
import { Alert, Box, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import {
  CourtEmailContent,
  LetterCondition,
  LettersMeta,
} from '../../api/LettersApi';
import LetterBlockEditor from './LetterBlockEditor';

/**
 * The email that carries the letter to the court.
 *
 * What is editable is what it says — subject, heading, paragraphs, where the ticket-details box
 * sits, and the sign-off. The table layout email clients need is not editable and is not shown:
 * nobody should have to keep nested tables intact to change a sentence.
 */

interface Props {
  content: CourtEmailContent;
  meta: LettersMeta;
  readOnly: boolean;
  onChange: (content: CourtEmailContent) => void;
}

const CourtEmailEditor: React.FC<Props> = ({ content, meta, readOnly, onChange }) => (
  <Box>
    <Alert severity="info" sx={{ mb: 2 }}>
      This is the email a court clerk opens. The letter of representation is attached to it, along
      with the 39.14 request and preservation demand on not-guilty cases.
    </Alert>

    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          <TextField
            fullWidth
            size="small"
            label="Subject"
            disabled={readOnly}
            helperText="What the clerk sees in their inbox before opening it"
            value={content.subject}
            onChange={(e) => onChange({ ...content, subject: e.target.value })}
          />
          <TextField
            fullWidth
            size="small"
            label="Heading"
            disabled={readOnly}
            helperText="The green heading at the top of the email"
            value={content.heading}
            onChange={(e) => onChange({ ...content, heading: e.target.value })}
          />
        </Stack>
      </CardContent>
    </Card>

    <LetterBlockEditor
      content={{ blocks: content.blocks }}
      placeholders={meta.emailPlaceholders}
      conditions={meta.emailConditions as { value: LetterCondition; label: string }[]}
      readOnly={readOnly}
      allowDetailsBlock
      onChange={(next) => onChange({ ...content, blocks: next.blocks })}
    />

    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Sign-off
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            size="small"
            label="Closing line"
            disabled={readOnly}
            value={content.signOff}
            onChange={(e) => onChange({ ...content, signOff: e.target.value })}
          />
          <TextField
            fullWidth
            size="small"
            label="From"
            disabled={readOnly}
            value={content.signOffName}
            onChange={(e) => onChange({ ...content, signOffName: e.target.value })}
          />
        </Stack>
      </CardContent>
    </Card>

    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
      Tip: wrap words in **two asterisks** to make them bold, as the ticket number is today.
    </Typography>
  </Box>
);

export default CourtEmailEditor;
